import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Icon, cn, cssVars } from "@sagot/ui";
import type { ReactNode } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useBuildFormatters } from "../shared/format";
import { useAudienceLabels } from "../shared/queries";
import type { Trace, TraceChunk } from "../shared/types";
import { chunkCounts, formatScore, latencyBars, orderChunks, type LatencyKind } from "./trace-model";

const BAR_TONE: Readonly<Record<LatencyKind, string>> = { retrieval: "bg-brand-500", first: "bg-signal-500", generation: "bg-ink-700 dark:bg-ink-300" };
const PERCENT = 100;
const USD_DIGITS = 4;

export function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="border-b border-line px-4 py-4">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-fg-subtle">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function QuerySection({ trace }: { trace: Trace }) {
  const { t } = useTranslation();
  return (
    <Section title={t("build.playground.trace.query")} aside={trace.language ? <Badge><Icon name="languages" className="size-3" />{t("build.playground.trace.language", { language: trace.language })}</Badge> : undefined}>
      <div className="space-y-2">
        <div>
          <div className="mb-1 text-[11px] font-semibold text-fg-muted">{t("build.playground.trace.asked")}</div>
          <div className="rounded-md bg-sunken px-3 py-2 text-xs">{trace.query ?? "—"}</div>
        </div>
        <div>
          <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-fg-muted"><Icon name="sparkles" className="size-3 text-primary-ink" />{t("build.playground.trace.rewritten")}</div>
          <div className="rounded-md border border-line px-3 py-2 font-mono text-[11px] leading-relaxed">{trace.rewritten ?? t("build.playground.trace.notRewritten")}</div>
        </div>
      </div>
    </Section>
  );
}

export function LatencySection({ trace }: { trace: Trace }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const { bars, totalMs } = latencyBars(trace.latency);
  if (bars.length === 0) return null;
  return (
    <Section title={t("build.playground.trace.latency")} aside={<span className="font-mono text-[11px] font-semibold">{t("build.playground.trace.total", { value: fmt.duration(totalMs) })}</span>}>
      <ul className="space-y-1.5">
        {bars.map((bar) => (
          <li key={bar.stage} className="grid grid-cols-[7.5rem_1fr_3.5rem] items-center gap-2 text-[11px]">
            <span className="truncate text-fg-muted" title={bar.stage}>{bar.stage}</span>
            <span className="relative h-2.5 overflow-hidden rounded-xs bg-sunken" aria-hidden="true">
              <span className={cn("absolute inset-y-0 left-[var(--geo-left)] w-[var(--geo-width)] rounded-xs", BAR_TONE[bar.kind])} style={cssVars({ "--geo-left": `${String(bar.left)}%`, "--geo-width": `${String(bar.width)}%` })} />
            </span>
            <span className="text-right font-mono tabular-nums">{fmt.duration(bar.ms)}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-3 pt-2 text-[10px] font-semibold text-fg-subtle" aria-hidden="true">
        <span className="flex items-center gap-1"><span className="size-2 rounded-xs bg-brand-500" />{t("build.playground.trace.legendRetrieval")}</span>
        <span className="flex items-center gap-1"><span className="size-2 rounded-xs bg-signal-500" />{t("build.playground.trace.legendFirst")}</span>
        <span className="flex items-center gap-1"><span className="size-2 rounded-xs bg-ink-700 dark:bg-ink-300" />{t("build.playground.trace.legendGeneration")}</span>
      </div>
    </Section>
  );
}

export function AccessSection({ trace }: { trace: Trace }) {
  const { t } = useTranslation();
  const label = useAudienceLabels();
  const audiences = trace.audiences.map(label).join(", ") || "—";
  return (
    <Section title={t("build.playground.trace.access")} aside={<Badge tone={trace.cache ? "brand" : "neutral"}>{trace.cache ? t("build.playground.trace.cache", { kind: trace.cache }) : t("build.playground.trace.cacheMiss")}</Badge>}>
      <div className="flex items-start gap-2 rounded-md bg-surface-2 px-3 py-2 text-[11px] leading-relaxed text-fg-muted">
        <Icon name="shield-check" className="mt-px size-3.5 shrink-0 text-primary-ink" />
        <span>{t("build.playground.trace.accessBody", { audiences })}</span>
      </div>
    </Section>
  );
}

function ChunkCard({ chunk, index }: { chunk: TraceChunk; index: number }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const rank = (value: number | null | undefined) => (value === null || value === undefined ? "—" : t("build.playground.trace.rank", { value }));
  const cells = [
    [t("build.playground.trace.ranks.dense"), rank(chunk.dense_rank)],
    [t("build.playground.trace.ranks.lexical"), rank(chunk.lexical_rank)],
    [t("build.playground.trace.ranks.question"), rank(chunk.question_rank)],
    [t("build.playground.trace.ranks.rrf"), formatScore(chunk.rrf, 3)],
    [t("build.playground.trace.ranks.rerank"), formatScore(chunk.rerank_score)],
  ] as const;
  const title = chunk.title ?? t("build.playground.trace.untitled");
  return (
    <li className={cn("rounded-md border p-2.5", chunk.used ? "border-line-strong" : "border-line")} data-testid="trace-chunk">
      <div className="flex items-start gap-2">
        <span className="mt-px font-mono text-[10px] font-bold text-fg-subtle">#{index + 1}</span>
        <Icon name="file-text" className="mt-px size-3.5 shrink-0 text-primary-ink" />
        <div className="min-w-0 flex-1">
          {chunk.source_id ? (
            <AppLink href={consoleRoutes.sourceDetail(summary.slug, chunk.source_id)} className="block truncate text-xs font-bold hover:text-primary-ink">{title}</AppLink>
          ) : <span className="block truncate text-xs font-bold">{title}</span>}
        </div>
        <Badge tone={chunk.used ? "success" : "neutral"} className="shrink-0">
          <Icon name={chunk.used ? "check-circle" : "minus"} className="size-3" />
          {chunk.used ? t("build.playground.trace.used") : t("build.playground.trace.filtered")}
        </Badge>
      </div>
      <dl className="mt-2 grid grid-cols-5 gap-1 text-center font-mono text-[10px]">
        {cells.map(([name, value]) => (
          <div key={name} className="rounded-xs bg-sunken py-1">
            <dt className="text-fg-subtle">{name}</dt>
            <dd className="font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
    </li>
  );
}

export function ChunksSection({ trace }: { trace: Trace }) {
  const { t } = useTranslation();
  const counts = chunkCounts(trace.chunks);
  return (
    <Section title={t("build.playground.trace.chunks")} aside={<span className="text-[11px] font-semibold text-fg-muted">{t("build.playground.trace.chunkCounts", counts)}</span>}>
      {trace.chunks.length === 0 ? <p className="text-xs text-fg-muted">{t("build.playground.trace.noChunks")}</p> : (
        <ol className="space-y-2">{orderChunks(trace.chunks).map((chunk, i) => <ChunkCard key={chunk.chunk_id} chunk={chunk} index={i} />)}</ol>
      )}
    </Section>
  );
}

export function TokensSection({ trace }: { trace: Trace }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const tokens = trace.tokens as Record<string, number | undefined>;
  const cells = [
    [t("build.playground.trace.input"), fmt.number(tokens.in ?? 0)],
    [t("build.playground.trace.output"), fmt.number(tokens.out ?? 0)],
    [t("build.playground.trace.cost"), trace.cost_usd === null ? "—" : `$${trace.cost_usd.toFixed(USD_DIGITS)}`],
  ] as const;
  const rows = [
    [t("build.playground.trace.model"), trace.model],
    [t("build.playground.trace.embedding"), trace.embedding],
    [t("build.playground.trace.reranker"), trace.reranker],
    [t("build.playground.trace.search"), trace.search_mode],
  ] as const;
  return (
    <Section title={t("build.playground.trace.tokens")}>
      <div className="grid grid-cols-3 gap-2 text-center">
        {cells.map(([name, value]) => (
          <div key={name} className="rounded-md bg-surface-2 py-2">
            <div className="text-[10px] font-semibold text-fg-subtle">{name}</div>
            <div className="font-mono text-sm font-bold tabular-nums">{value}</div>
          </div>
        ))}
      </div>
      <dl className="mt-3 space-y-1.5 text-[11px]">
        {rows.map(([name, value]) => (
          <div key={name} className="flex justify-between gap-3"><dt className="text-fg-subtle">{name}</dt><dd className="truncate font-mono">{value ?? "—"}</dd></div>
        ))}
      </dl>
    </Section>
  );
}

export function GroundingSection({ trace }: { trace: Trace }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const grounded = trace.grounded !== false;
  const confidence = trace.confidence ?? 0;
  return (
    <Section title={t("build.playground.trace.grounding")}>
      <div className="flex items-baseline justify-between">
        <span className={cn("flex items-center gap-1 text-sm font-bold", grounded ? "text-success" : "text-warning")}>
          <Icon name={grounded ? "check-circle" : "alert-triangle"} className="size-4" />
          {grounded ? t("build.playground.trace.grounded") : t("build.playground.trace.notGrounded")}
        </span>
        <span className="font-mono text-xs font-semibold">{t("build.playground.trace.confidence", { value: formatScore(trace.confidence) })}</span>
      </div>
      <div className="mt-2 h-2 rounded-pill bg-sunken" aria-hidden="true">
        <div className={cn("h-2 w-[var(--geo-width)] rounded-pill", grounded ? "bg-mint-500" : "bg-signal-500")} style={cssVars({ "--geo-width": `${String(Math.round(confidence * PERCENT))}%` })} />
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-fg-muted">
        {grounded ? t("build.playground.trace.groundedBody") : (
          <>{t("build.playground.trace.ungroundedBody")} <AppLink href={consoleRoutes.gaps(summary.slug)} className="font-semibold text-primary-ink hover:underline">{t("build.playground.trace.gapsLink")}</AppLink></>
        )}
      </p>
    </Section>
  );
}
